import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment';

import { ReactComponent as CheckIcon } from '../../../assets/images/checkIcon.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/InfoIconShaped.svg';
import { ReactComponent as RespondIcon } from '../../../assets/images/respond.svg';
import BackButton from '../../../components/shared/BackButton/BackButton';
import Button from '../../../components/shared/Button/Button';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import { CURRENCIES } from '../../../constants/currenciesConstants';
import { getDisputeDetails, postDeclineRequestClose, postDisputeClose } from '../../../redux/actions/disputeActions';
import CloseDispute from '../CloseDispute/CloseDispute';
import DeclineDisputeCloseModal from '../DeclineDisputeClose/DeclineDisputeClose';

import styles from './DisputeDetails.module.scss';

const DisputeDetails = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const params = useParams();
  const { id: disputeId } = params;
  const disputeDetails = useSelector((state) => state.disputesReducer.disputeDetails);
  const [closeDisputeModal, setCloseDisputeModal] = useState(false);
  const [declineRequestModal, setDeclineRequestModal] = useState(false);

  useEffect(() => {
    dispatch(getDisputeDetails(disputeId));
  }, [disputeId]);

  const toggleCloseDisputeModal = useCallback(() => {
    setCloseDisputeModal((prevState) => !prevState);
  }, []);

  const handleDisputeClose = useCallback(() => {
    dispatch(
      postDisputeClose(disputeId, () => {
        dispatch(getDisputeDetails(disputeId));
        toggleCloseDisputeModal();
      })
    );
  }, [disputeId]);

  const toggleDeclineRequestModal = useCallback(() => {
    setDeclineRequestModal((prevState) => !prevState);
  }, []);

  const handleDeclineRequestClose = useCallback(
    (data) => {
      dispatch(postDeclineRequestClose({ ...data, disputeId }, toggleDeclineRequestModal));
    },
    [toggleDeclineRequestModal, declineRequestModal, disputeId]
  );

  return (
    <div className={styles.rootCont}>
      <BackButton />
      <CloseDispute
        show={closeDisputeModal}
        onHide={toggleCloseDisputeModal}
        data={disputeDetails}
        handleSubmit={handleDisputeClose}
      />
      <DeclineDisputeCloseModal
        show={declineRequestModal}
        onHide={toggleDeclineRequestModal}
        data={disputeDetails}
        handleSubmit={handleDeclineRequestClose}
      />
      <div className={styles.contentWrapper}>
        <div className={styles.contentCont}>
          <h1 className={styles.heading}>{t('disputeDetails.referralRewardSummary')}</h1>
          <div className={styles.innerCont}>
            <div className={styles.infoBlock}>
              <div className={styles.infoSubBlock}>
                <p className={styles.title}>{t('dispute.companyName')}</p>
                <p className={classNames(styles.value, styles.fontHeavy)}>{disputeDetails.employer}</p>
              </div>
              <div className={styles.infoSubBlock}>
                <p className={styles.title}>{t('dispute.disputeId')}</p>
                <p className={styles.value}>{disputeDetails.uuid}</p>
              </div>
            </div>

            <div className={styles.infoBlock}>
              <div className={styles.infoSubBlock}>
                <p className={styles.title}>{t('dispute.candidateNameLabel')}</p>
                <p className={styles.value}>{disputeDetails.candidate}</p>
              </div>
              <div className={styles.infoSubBlock}>
                <p className={styles.title}>{t('dispute.openingTitleLabel')}</p>
                <p className={styles.value}>{disputeDetails.opening}</p>
              </div>
            </div>

            <div className={styles.infoBlock}>
              <div className={styles.infoSubBlock}>
                <p className={styles.title}>{t('dispute.disputeDate')}</p>
                <p className={styles.value}>{moment(disputeDetails.created_at).format('MM/DD/YY')}</p>
              </div>
              <div className={styles.infoSubBlock}>
                <p className={styles.title}>{t('disputeDetails.resolutionDate')}</p>
                <p className={styles.value}>
                  {disputeDetails.resolved_at ? moment(disputeDetails.resolved_at).format('MM/DD/YY') : '---'}
                </p>
              </div>
            </div>

            <div className={styles.infoBlock}>
              <div className={styles.infoSubBlock}>
                <p className={styles.title}>{t('disputeDetails.disputeReason')}</p>
                <p className={classNames(styles.value, styles.textCapitilize)}>
                  {disputeDetails.reason?.split('_').join(' ') || '---'}
                </p>
              </div>
              <div className={styles.infoSubBlock}>
                <p className={styles.title}>{t('disputeDetails.disputeStatus')}</p>
                <div className={styles.statusCont}>
                  {disputeDetails.request_to_close && disputeDetails.open ? (
                    <StatusBadge
                      type="dispute"
                      value="respond"
                      icon={RespondIcon}
                      onClick={toggleDeclineRequestModal}
                    />
                  ) : (
                    <StatusBadge
                      type="payment"
                      value={disputeDetails.open ? 'disputed' : 'resolved'}
                      icon={disputeDetails.open ? InfoIcon : CheckIcon}
                    />
                  )}
                </div>
              </div>
            </div>

            <p className={styles.title}>{t('description')}:</p>
            <p className={styles.value}>{disputeDetails.details || '---'}</p>

            <div className={styles.divider} />

            <p className={styles.title}>{t('disputeDetails.currentRewardStatus')}</p>
            <div className={`payout-status ${disputeDetails.current_reward_status}`}>
              <div className={styles.statusCont}>
                <StatusBadge type="payment" value={disputeDetails.current_reward_status} />
              </div>
            </div>

            <p className={styles.title}>{t('disputeDetails.rewardDetails')}</p>
            <p className={styles.value}>
              {CURRENCIES[disputeDetails.reward_currency]}
              {disputeDetails.reward}
            </p>
          </div>
          <div className={styles.supportCTA}>
            <a href={process.env.REACT_APP_DISPUTES_PORTAL_URL} target="_blank" rel="noreferrer">
              {t('disputeDetails.contactSupport')}
            </a>
            &nbsp;{t('disputeDetails.forAssistance')}
          </div>
        </div>
        {disputeDetails.open && (
          <div className={styles.disputeActions}>
            <Button type="button" onClick={toggleCloseDisputeModal} variant="outline-primary">
              {t('dispute.closeDispute')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DisputeDetails;
